// Well-defined tasks that require alternative processing pipeline
// These tasks have standardized workflows and don't need the complex
// variant selection, amount clarification, and location clarification steps

import { t, Lang } from '../../../../i18n';

// Core task definitions - defined once with unique IDs
export const WELL_DEFINED_TASK_IDS = [
  'property_connection',
  'manhole_replacement',
  'manhole_connection_replacement',
  'hole_subsidence',
  'main_sewer',
  'drainage',
  'clean_manholes'
] as const;

export type WellDefinedTaskId = typeof WELL_DEFINED_TASK_IDS[number];

// Helper function to get task name using i18n
export const getWellDefinedTaskName = (taskId: WellDefinedTaskId, lang: Lang): string => {
  const i18nKey = `well_defined_task_${taskId}` as any;
  return t(lang, i18nKey);
};

// Helper function to get all well-defined tasks for a language (using i18n)
export const getWellDefinedTasks = (lang: Lang): Array<{ id: WellDefinedTaskId; name: string }> => {
  return WELL_DEFINED_TASK_IDS.map(id => ({
    id,
    name: getWellDefinedTaskName(id, lang)
  }));
};

// Helper function to check if a task description matches a well-defined task
export const isWellDefinedTask = (taskDescription: string, lang: Lang): boolean => {
  const normalizedDescription = taskDescription.toLowerCase().trim();
  const tasks = getWellDefinedTasks(lang);
  
  return tasks.some(task => {
    const taskName = task.name.toLowerCase();
    return normalizedDescription.includes(taskName) ||
           taskName.includes(normalizedDescription);
  });
};

// Helper function to get the well-defined task ID from a description
export const getWellDefinedTaskId = (taskDescription: string, lang: Lang): WellDefinedTaskId | null => {
  const normalizedDescription = taskDescription.toLowerCase().trim();
  const tasks = getWellDefinedTasks(lang);
  
  const matchingTask = tasks.find(task => {
    const taskName = task.name.toLowerCase();
    return normalizedDescription.includes(taskName) ||
           taskName.includes(normalizedDescription);
  });
  
  return matchingTask?.id || null;
};

// Legacy type alias for backward compatibility
export type WellDefinedTaskType = WellDefinedTaskId;

// Subtask structure for each well-defined task
export interface Subtask {
  id: string; // Unique subtask ID (e.g., 'install_connection')
  i18nKey: string; // i18n key for the subtask name (e.g., 'subtask_install_connection')
  descriptionI18nKey?: string; // Optional i18n key for description
}

// Common subtasks for all tasks except clean_manholes
// These subtasks are shared across property_connection, manhole_replacement, 
// manhole_connection_replacement, hole_subsidence, main_sewer, and drainage
const COMMON_SUBTASKS: Subtask[] = [
  { id: '141', i18nKey: 'subtask_141' },
  { id: '142', i18nKey: 'subtask_142' },
  { id: '143', i18nKey: 'subtask_143' },
  { id: '144', i18nKey: 'subtask_144' },
  { id: '145', i18nKey: 'subtask_145' },
  { id: '146', i18nKey: 'subtask_146' },
  { id: '147', i18nKey: 'subtask_147' },
  { id: '149', i18nKey: 'subtask_149' },
  { id: '147110', i18nKey: 'subtask_147110' },
  { id: '151', i18nKey: 'subtask_151' },
  { id: '181', i18nKey: 'subtask_181' },
  { id: '182', i18nKey: 'subtask_182' },
  { id: '183', i18nKey: 'subtask_183' },
  { id: '184', i18nKey: 'subtask_184' },
  { id: '185010', i18nKey: 'subtask_185010' },
  { id: '343', i18nKey: 'subtask_343' },
  { id: '344', i18nKey: 'subtask_344' },
  { id: '345', i18nKey: 'subtask_345' }
];

// Task-specific subtasks
const SUBTASK_24: Subtask = { id: '24', i18nKey: 'subtask_24' }; // Installing Drainage manhole / wells
const SUBTASK_154: Subtask = { id: '154', i18nKey: 'subtask_154' }; // installation of Shut-off valves
const SUBTASK_155: Subtask = { id: '155', i18nKey: 'subtask_155' }; // Flushing drainage pipes
const SUBTASK_163: Subtask = { id: '163', i18nKey: 'subtask_163' }; // Soil Excavation (House and manhole connections, pressure pipe, drainage)
const SUBTASK_164: Subtask = { id: '164', i18nKey: 'subtask_164' }; // Soil Excavation (Formwork - Shoring trench sewer, double sided with different heights)
const SUBTASK_211: Subtask = { id: '211', i18nKey: 'subtask_211' }; // installing piping - pipes
const SUBTASK_212: Subtask = { id: '212', i18nKey: 'subtask_212' }; // Installing piping - House and manhole / well connections
const SUBTASK_214: Subtask = { id: '214', i18nKey: 'subtask_214' }; // installation of drainage pipes
const SUBTASK_221: Subtask = { id: '221', i18nKey: 'subtask_221' }; // Installing piping - Concrete manhole / wells
const SUBTASK_222: Subtask = { id: '222', i18nKey: 'subtask_222' }; // Installing Piping - Plastic wells
const SUBTASK_223: Subtask = { id: '223', i18nKey: 'subtask_223' }; // Installing piping - manhole / wells adjustments
const SUBTASK_224: Subtask = { id: '224', i18nKey: 'subtask_224' }; // Installing piping - Well edges
const SUBTASK_228010: Subtask = { id: '228010', i18nKey: 'subtask_228010' }; // Measuring wells
const SUBTASK_231: Subtask = { id: '231', i18nKey: 'subtask_231' }; // processing sand and soil - Main sewer and culverts
const SUBTASK_232: Subtask = { id: '232', i18nKey: 'subtask_232' }; // processing sand/soil - House and Drainage manhole / wells connection, pressure pipe
const SUBTASK_233: Subtask = { id: '233', i18nKey: 'subtask_233' }; // Drainage guidance - filling trenches
const SUBTASK_243: Subtask = { id: '243', i18nKey: 'subtask_243' }; // Attaching odor seal and replacing cover pin
const SUBTASK_251: Subtask = { id: '251', i18nKey: 'subtask_251' }; // Sewer inspection - pipes
const SUBTASK_260010: Subtask = { id: '260010', i18nKey: 'subtask_260010' }; // Fixing and repairing leaks and other damage within sewer systems
const SUBTASK_1558: Subtask = { id: '1558', i18nKey: 'subtask_1558' }; // Clean catch basins/gullies

// Mapping of well-defined tasks to their subtasks (language-agnostic)
// Structure: { [taskId]: Subtask[] }
// Each task has COMMON_SUBTASKS plus task-specific subtasks
export const WELL_DEFINED_TASKS_SUBTASKS: Record<WellDefinedTaskId, Subtask[]> = {
  property_connection: [...COMMON_SUBTASKS, SUBTASK_163, SUBTASK_164, SUBTASK_212, SUBTASK_222, SUBTASK_223, SUBTASK_232, SUBTASK_251],
  manhole_replacement: [...COMMON_SUBTASKS, SUBTASK_24, SUBTASK_243],
  manhole_connection_replacement: [...COMMON_SUBTASKS, SUBTASK_163, SUBTASK_164, SUBTASK_212, SUBTASK_223, SUBTASK_232, SUBTASK_251],
  hole_subsidence: [...COMMON_SUBTASKS, SUBTASK_163, SUBTASK_164, SUBTASK_154, SUBTASK_155, SUBTASK_212, SUBTASK_224, SUBTASK_231, SUBTASK_232, SUBTASK_260010],
  main_sewer: [...COMMON_SUBTASKS, SUBTASK_154, SUBTASK_163, SUBTASK_164, SUBTASK_211, SUBTASK_221, SUBTASK_222, SUBTASK_223, SUBTASK_224, SUBTASK_228010, SUBTASK_231, SUBTASK_251, SUBTASK_260010],
  drainage: [...COMMON_SUBTASKS, SUBTASK_155, SUBTASK_163, SUBTASK_164, SUBTASK_214, SUBTASK_233],
  clean_manholes: [SUBTASK_1558] // Only task-specific subtask for clean_manholes
};

// Helper function to get subtasks for a well-defined task ID
export const getSubtasksForTask = (
  taskId: WellDefinedTaskId,
  lang: Lang
): Array<{ id: string; name: string; description?: string }> => {
  const subtasks = WELL_DEFINED_TASKS_SUBTASKS[taskId] || [];
  
  // Map subtasks to include translated names
  const mappedSubtasks = subtasks.map(subtask => ({
    id: subtask.id,
    name: t(lang, subtask.i18nKey as any),
    description: subtask.descriptionI18nKey ? t(lang, subtask.descriptionI18nKey as any) : undefined
  }));
  
  // Sort by numeric ID (parse as number for proper numeric sorting)
  return mappedSubtasks.sort((a, b) => {
    const idA = parseFloat(a.id) || 0;
    const idB = parseFloat(b.id) || 0;
    return idA - idB;
  });
};

// Helper function to get subtasks for a well-defined task by matching description
export const getSubtasksForTaskDescription = (
  taskDescription: string,
  lang: Lang
): Array<{ id: string; name: string; description?: string }> => {
  const taskId = getWellDefinedTaskId(taskDescription, lang);
  if (!taskId) {
    return [];
  }
  
  return getSubtasksForTask(taskId, lang);
};